import { useContext } from "react";
import { useSelectAccountsStates } from "@/features/accounts/shared/hooks/use-select-accounts-states";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import {
  ErrorDialogContext,
  errorHandledAsync,
  errorHandledSync,
} from "@/shared/context/error-context";
import { usePutFinanceAccount } from "@/features/accounts/finance/hooks/use-put-accounts";
import { useDeleteFinanceAccount } from "@/features/accounts/finance/hooks/use-delete-accounts";
import { useUpdateCrecaAccounts } from "@/features/accounts/creca/hooks/use-put-accounts";
import { useDeleteCrecaAccount } from "@/features/accounts/creca/hooks/use-delete-accounts";
import { useUpdatePaymentAccounts } from "@/features/accounts/payment/scraping/hooks/use-update-accounts";
import { useDeletePaymentAccounts } from "@/features/accounts/payment/scraping/hooks/use-delete-accounts";
import { usePutEcshopAccounts } from "@/features/accounts/ecshop/hooks/use-put-accounts";
import { useDeleteEcshopAccounts } from "@/features/accounts/ecshop/hooks/use-delete-accounts";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";


export interface AccountsInfo {
  accountGroupId: string;
  mlinkAccountId: string;
  mlinkId: string;
  accountKindName: string;
  accountNo: string;
  selectedStatus: number;
  //accountType: "finance" | "creca" | "payment";
  passbookType: string;
  userInputName: string;
  scrapeType: number; // 0：なし、1：インストール版スクレイピング（対象なし）、2：サーバ版スクレイピング、3：API版
  otpYn: string; // ワンパス発生有無 0：OTP画面は表示されない, 1：必ずOTP画面が表示される, 2：ユーザー設定次第でOTP画面が表示される
}

interface Props {
  onTapCompleteModal: (accounts: AccountsInfo[], mitsubishiUfjCorp: string) => void;
}

export const useSelectAccounts = ({onTapCompleteModal}: Props) => {
  const {
    isActiveRegisterButton,
    setIsActiveRegisterButton,
  } = useSelectAccountsStates();

  const { updateFinanceAccounts } = usePutFinanceAccount();
  const { deleteFinanceAccounts } = useDeleteFinanceAccount();
  const { updateCrecaAccounts } = useUpdateCrecaAccounts();
  const { deleteCrecaAccounts } = useDeleteCrecaAccount();
  const { updatePaymentAccounts } = useUpdatePaymentAccounts();
  const { deletePaymentAccounts } = useDeletePaymentAccounts();
  const { updateEcshopAccounts } = usePutEcshopAccounts();
  const { deleteEcshopAccounts } = useDeleteEcshopAccounts();

  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  const onChangeSelectAccount = (selected_status: number) => {
    if (selected_status === 0) {
      selected_status = 1;
    } else {
      selected_status = 0;
    }
  }

  // ボタン押下 → チェックされている要素に対してAPI実行
  const onClickRegister = async (
    lists: AccountsInfo[]
  ) => {
    loadingCtx.setIsLoading(true);
    const mitsubishiUfjCorp = localStorage.getItem("MitsubishiUfjCorp") || "";
    // 各API関数は errorHandledAsync でラップ済み、エラー時は undefined を返す
    let hasError = false;

    for (const list of lists) {
      let result: unknown;

      // 銀行API
      if (list.passbookType === "01") {
        // 銀行API口座は選択された口座のみ選択状態に更新
        if (list.selectedStatus === 1) {
          result = await updateFinanceAccounts(
            list.accountGroupId,
            list.mlinkAccountId,
            list.mlinkId,
            1,
            undefined, // isAggregate
            (list.userInputName !== "" ? list.userInputName : `${list.accountKindName} ${list.accountNo}`),
          );
        } else {
          result = true; // 処理不要の場合は成功扱い
        }
      }
      // クレカAPI
      else if (list.passbookType === "19659" || list.passbookType === "19660") {
        // クレカAPI口座は選択された口座のみ選択状態に更新
        if (list.selectedStatus === 1) {
          result = await updateCrecaAccounts(
            list.accountGroupId,
            list.mlinkAccountId,
            1,
            (list.userInputName !== "" ? list.userInputName : `${list.accountKindName} ${list.accountNo}`),
          );
        } else {
          result = true;
        }
      }
      // Look口座
      else if (list.passbookType === "03" || list.passbookType === "10") {
        // Look口座は選択された&口座名を変更した口座は更新、選択されなかった口座は削除
        if (list.selectedStatus === 1 && list.userInputName !== "") {
          result = await updatePaymentAccounts(
            list.accountGroupId,
            list.mlinkAccountId,
            list.mlinkId,
            (list.userInputName !== "" ? list.userInputName : `${list.accountKindName} ${list.accountNo}`),
          );
        } else if (list.selectedStatus !== 1) {
          result = await deletePaymentAccounts(
            list.accountGroupId,
            list.mlinkAccountId,
            list.mlinkId,
          );
        } else {
          result = true;
        }
      }
      // その他連携口座（ECショップ）
      else if (list.passbookType === "20") {
        // その他連携口座は選択された口座のみ選択状態に更新
        if (list.selectedStatus === 1) {
          result = await updateEcshopAccounts(
            list.accountGroupId,
            list.mlinkAccountId,
            list.mlinkId,
            1,
            (list.userInputName !== "" ? list.userInputName : `${list.accountKindName} ${list.accountNo}`),
          );
        } else {
          result = true;
        }
      } else {
        result = true; // 未対応のpassbookTypeは成功扱い
      }

      // エラー発生時（undefined）はフラグを立てて続行（全件処理する）
      if (result === undefined) {
        hasError = true;
      }
    }

    loadingCtx.setIsLoading(false);

    if (hasError) {
      // エラー時はカスタムメッセージを表示（ホーム画面に戻り，正常終了した口座は見せる必要がある）
      setError(new MoneyLinkWarning({
        title: "エラー",
        message: "登録操作が完了しなかった口座があります",
      }));
    }

    // エラーがあっても成功した口座でホーム画面に戻る
    onTapCompleteModal(lists.filter((item) => item.selectedStatus === 1), mitsubishiUfjCorp);
  };

  // 登録キャンセル（削除APIが失敗してもダイアログは表示せず業務続行）
  const cancelRegistration = async (
    lists: AccountsInfo[]
  ) => {
    for (const list of lists) {
      if (list.passbookType === "01") {
        await deleteFinanceAccounts(
          list.accountGroupId,
          list.mlinkAccountId,
          list.mlinkId,
        );
      } else if (list.passbookType === "19659" || list.passbookType === "19660") {
        await deleteCrecaAccounts(
          list.accountGroupId,
          list.mlinkAccountId,
        );
      } else if (list.passbookType === "03" || list.passbookType === "10") {
        await deletePaymentAccounts(
          list.accountGroupId,
          list.mlinkAccountId,
          list.mlinkId,
        );
      } else if (list.passbookType === "20") {
        await deleteEcshopAccounts(
          list.accountGroupId,
          list.mlinkAccountId,
        );
      }
    }
    // エラーが発生してもダイアログを表示しないようにクリア
    setError(undefined);
  };

  const resetLocalStorage = () => {
    localStorage.removeItem("industryCode");
    localStorage.removeItem("companyCode");
    localStorage.removeItem("serviceCode");
    localStorage.removeItem("companyName");
    localStorage.removeItem("serviceName");
    localStorage.removeItem("branchName");
    localStorage.removeItem("MitsubishiUfjCorp");
  };

  return {
    isActiveRegisterButton,
    setIsActiveRegisterButton,
    onChangeSelectAccount: errorHandledSync(onChangeSelectAccount, setError),
    onClickRegister: errorHandledAsync(onClickRegister, setError),
    cancelRegistration: errorHandledAsync(cancelRegistration, setError),
    resetLocalStorage,
  };
};
